import React from 'react'
import { isEmpty } from 'lodash-es'
import { intl } from 'lang/intl'

import Button from '../../sharedLegacy/components/ButtonComponent/ButtonComponent'
import { useWebinarColors } from '../../Designs/blocs/useWebinarColors'
import { type Poll, type PollState } from '../model/Poll'

import { PollBody } from './PollBody'
import { PollOverlayTrigger } from './PollOverlayTrigger'
import { usePollsContext } from './PollsContext'

import './PollItem.scss'

type PollItemProps = {
  poll: Poll
}

const getTitle = (poll: Poll) => {
  const dot = `·`
  const count = poll.answerCount

  const votes =
    count === 1
      ? intl.formatMessage({ defaultMessage: `{count} vote` }, { count })
      : intl.formatMessage({ defaultMessage: `{count} votes` }, { count })

  if (poll.open) {
    const message = intl.formatMessage({ defaultMessage: `Open for voting` })
    return `${message} ${dot} ${votes}`
  }

  if (poll.displayResults) {
    const message = intl.formatMessage({ defaultMessage: `Results showing` })
    return `${message} ${dot} ${votes}`
  }

  const prefix = intl.formatMessage({ defaultMessage: `Poll` })

  if (!isEmpty(poll.openedDate)) {
    return `${prefix} · ${votes}`
  }

  const suffix = intl.formatMessage({ defaultMessage: `Not started` })
  return `${prefix} · ${suffix}`
}

const pollButtonText: Record<PollState, () => string> = {
  start: () => intl.formatMessage({ defaultMessage: 'Start Poll' }),
  finish: () => intl.formatMessage({ defaultMessage: 'Close Poll' }),
  publish: () => intl.formatMessage({ defaultMessage: 'Show Results' }),
  unpublish: () =>
    intl.formatMessage({ defaultMessage: 'Stop Showing Results' }),
}

const getPollButtonText = (poll: Poll) => pollButtonText[poll.state]()

export const PollItem = ({ poll }: PollItemProps) => {
  const {
    renderPollUnitWrapper,
    canChangePoll,
    disableOverlayTrigger,
    activatePoll,
  } = usePollsContext()
  const { accent } = useWebinarColors()

  const showOverlayTrigger =
    canChangePoll && !disableOverlayTrigger && !isEmpty(poll.openedDate)

  const renderHeaderAction = () =>
    showOverlayTrigger ? (
      <div className="header-actions-wrapper">
        <PollOverlayTrigger poll={poll} />
      </div>
    ) : null

  const children = (
    <>
      <PollBody poll={poll} />
      {canChangePoll && (
        <Button
          style={{
            backgroundColor: accent.color,
            color: accent.contrast.color,
          }}
          onClick={() => activatePoll(poll)}
          color="transparent"
          className="poll-button"
          round
        >
          {getPollButtonText(poll)}
        </Button>
      )}
    </>
  )

  return (
    <div className="poll-item-wrapper-component">
      {renderPollUnitWrapper
        ? renderPollUnitWrapper({
            title: getTitle(poll),
            isPollActive: poll.open || poll.displayResults,
            renderHeaderAction,
            children,
          })
        : children}
    </div>
  )
}
